/**
 * Chakra UI slider helper utilities.
 * Modified and converted to plain Javascript.
 *
 * @see Docs     https://chakra-ui.com/docs/form/slider
 * @see Source   https://github.com/chakra-ui/chakra-ui/blob/main/packages/slider/src/slider-utils.ts
 * @see WAI-ARIA https://www.w3.org/TR/wai-aria-practices/#slider
 */

export function getIds(id) {
  return {
    root: `slider-root-${id}`,
    getThumb: i => `slider-thumb-${id}-${i}`,
    getInput: i => `slider-input-${id}-${i}`,
    track: `slider-track-${id}`,
    innerTrack: `slider-filled-track-${id}`,
    getMarker: i => `slider-marker-${id}-${i}`,
    output: `slider-output-${id}`
  }
}

export function orient(options) {
  const { orientation, vertical, horizontal } = options
  return orientation === 'vertical' ? vertical : horizontal
}

const zeroRect = { width: 0, height: 0 }

export function getStyles(options) {
  const { orientation, thumbPercents, thumbRects, isReversed } = options

  const getThumbStyle = i => ({
    position: 'absolute',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    MozUserSelect: 'none',
    msUserSelect: 'none',
    touchAction: 'none',
    ...orient({
      orientation,
      vertical: {
        bottom: `calc(${thumbPercents[i]}% - ${thumbRects[i].height / 2}px)`
      },
      horizontal: {
        left: `calc(${thumbPercents[i]}% - ${thumbRects[i].width / 2}px)`
      }
    })
  })

  const size =
    orientation === 'vertical'
      ? thumbRects.reduce((a, b) => (a.height > b.height ? a : b), zeroRect)
      : thumbRects.reduce((a, b) => (a.width > b.width ? a : b), zeroRect)

  const rootStyle = {
    position: 'relative',
    touchAction: 'none',
    WebkitTapHighlightColor: 'rgba(0,0,0,0)',
    userSelect: 'none',
    outline: 0,
    ...orient({
      orientation,
      vertical: {
        paddingLeft: size.width / 2,
        paddingRight: size.width / 2
      },
      horizontal: {
        paddingTop: size.height / 2,
        paddingBottom: size.height / 2
      }
    })
  }

  const trackStyle = {
    position: 'absolute',
    ...orient({
      orientation,
      vertical: {
        left: '50%',
        transform: 'translateX(-50%)',
        height: '100%'
      },
      horizontal: {
        top: '50%',
        transform: 'translateY(-50%)',
        width: '100%'
      }
    })
  }

  const isSingleThumb = thumbPercents.length === 1
  const fallback = [0, isReversed ? 100 - thumbPercents[0] : thumbPercents[0]]
  const range = isSingleThumb ? fallback : thumbPercents

  let start = range[0]
  if (!isSingleThumb && isReversed) {
    start = 100 - start
  }
  const percent = Math.abs(range[range.length - 1] - range[0])

  const innerTrackStyle = {
    ...trackStyle,
    ...orient({
      orientation,
      vertical: isReversed
        ? { height: `${percent}%`, top: `${start}%` }
        : { height: `${percent}%`, bottom: `${start}%` },
      horizontal: isReversed
        ? { width: `${percent}%`, right: `${start}%` }
        : { width: `${percent}%`, left: `${start}%` }
    })
  }

  return { trackStyle, innerTrackStyle, rootStyle, getThumbStyle }
}

export function getIsReversed(options) {
  const { isReversed, direction, orientation } = options

  if (direction === 'ltr' || orientation === 'vertical') {
    return isReversed
  }

  return !isReversed
}

export function getDefaultValue(min, max) {
  return max < min ? min : min + (max - min) / 2
}
